import { useContext } from "react";
import { Box, Button } from "@mui/material";
import { Sync as SyncIcon } from "@mui/icons-material";
import { CreateFeatureLogs, SalesBranchReport } from "../../../global/API";
import { ToolContext } from "../../../../../core/context/ToolContext";
import { FeatureIdState } from "../../../toolUsage/global/GlobalFcn";

export const Generate = () => {
    const { tool_state, setup_state, report_state, global_component_state } = useContext(ToolContext);
    const { __SESSION } = tool_state;
    const { toolUser } = setup_state;
    const { form, generateForm, salesBranchData, salesBranchList } = report_state;
    const { snackBarLoader } = global_component_state;
    const tool_id = process.env.REACT_APP_TID_BIZ;

    const sendFeatureLogs = async () => {
        await CreateFeatureLogs({
            aid: __SESSION.data.ainfo.aid,
            path: `${FeatureIdState("reports")}->1`,
            feature_id: 1,
            feature_name: "branch-sales-report",
            tid: tool_id,
            company_id: __SESSION.data.uaccess[0].company_id,
            action: 2,
        });
    };

    const toNum = (v) => {
        const n = parseFloat(v);
        return Number.isFinite(n) ? n : 0;
    };

    const toDate = (v) => (v ? String(v).slice(0, 10) : "");

    const fetchAllPages = async (qsp, limit) => {
        let offset = 0;
        let allRows = [];
        let shouldContinue = true;
        while (shouldContinue) {
            const res = await SalesBranchReport({ ...qsp, limit, offset });
            if (!res || !Array.isArray(res) || res.length === 0) break;
            allRows = allRows.concat(res);
            offset += res.length;
            generateForm.set((prev) => ({ ...prev, offset }));
            if (res.length < limit) shouldContinue = false;
        }
        return allRows;
    };

    const formatRows = (rows) => {
        const users = new Map((toolUser?.data || []).map((u) => [Number(u.id), u]));
        const grouped = rows.reduce((acc, row) => {
            const branchId = row.company_branch_id;
            const date = toDate(row.created_at || row.transaction_date || row.date);
            const effectiveUserId = row.adjustment_staff_id ?? row.created_by;
            if (branchId == null || effectiveUserId == null || !date) return acc;
            const key = `${branchId}|${date}|${effectiveUserId}`;
            const isAdjusted = row.adjustment_staff_id != null;
            const user = users.get(Number(effectiveUserId));
            if (!acc[key]) {
                acc[key] = {
                    company_branch_id: branchId,
                    name: row.name || "",
                    date,
                    user_id: Number(effectiveUserId),
                    first_name: user?.first_name ?? row.first_name ?? "",
                    last_name: user?.last_name ?? row.last_name ?? "",
                    total_sales: 0,
                    adjusted_total_sales: 0,
                    actual_sales: null,
                    has_total_sales: false,
                    has_adjusted_sales: false,
                };
            }
            if (isAdjusted) {
                acc[key].adjusted_total_sales += toNum(row.total_sales);
                acc[key].has_adjusted_sales = true;
            } else {
                acc[key].total_sales += toNum(row.total_sales);
                acc[key].has_total_sales = true;
            }
            if (row.actual_sales != null) {
                acc[key].actual_sales = (acc[key].actual_sales ?? 0) + toNum(row.actual_sales);
            }
            return acc;
        }, {});

        return Object.values(grouped)
            .map((row) => ({
                ...row,
                total_sales: row.has_total_sales ? row.total_sales : null,
                adjusted_total_sales: row.has_adjusted_sales ? row.adjusted_total_sales : null,
                variance:
                    row.actual_sales == null
                        ? null
                        : (row.has_total_sales ? row.total_sales : 0) +
                          (row.has_adjusted_sales ? row.adjusted_total_sales : 0) -
                          row.actual_sales,
            }))
            .sort((a, b) => {
                if (a.date !== b.date) return a.date.localeCompare(b.date);
                if (a.name !== b.name) return String(a.name).localeCompare(String(b.name));
                return `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);
            });
    };

    const handleGenerate = async () => {
        if (form.data.to === "" || form.data.from === "" || form.data.to < form.data.from) {
            snackBarLoader.set({
                ...snackBarLoader.data,
                snackbar: true,
                msg: "Invalid Date!",
                type: "error",
            });
            return;
        }

        generateForm.set((prev) => ({ ...prev, infiniteLoad: true, offset: 0 }));
        salesBranchData.set([]);
        salesBranchList.set([]);

        try {
            const qsp = {
                branch_id: form.data.branch_id,
                company_id: __SESSION.data.uaccess[0].company_id,
                user_id: form.data.user_id,
                from: form.data.from,
                to: form.data.to,
            };
            const rows = await fetchAllPages(qsp, generateForm.data.limit);
            const formatted = formatRows(rows);
            salesBranchData.set(rows);
            salesBranchList.set(formatted);
            await sendFeatureLogs();
        } catch (error) {
            console.error(error);
            snackBarLoader.set({
                ...snackBarLoader.data,
                snackbar: true,
                msg: "Failed to generate report.",
                type: "error",
            });
        } finally {
            generateForm.set((prev) => ({ ...prev, infiniteLoad: false, offset: 0 }));
        }
    };

    return (
        <Box minHeight="55px" display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
            <Box
                id="walkthrough-branch-sales-report-step-5"
                onClick={handleGenerate}
                component={Button}
                disableFocusRipple
                minHeight="50px"
                minWidth="130px"
                borderRadius={3}
                display="flex"
                alignItems="center"
                justifyContent="center"
                bgcolor="primary.main"
                color="#fff"
                sx={{ border: "2px solid #EBEBEB", ":hover": { bgcolor: "primary.main", color: "#FFFFFF" } }}
                startIcon={<SyncIcon />}
            >
                <Box fontSize={16} fontWeight={400} lineHeight="19px">
                    Generate
                </Box>
            </Box>
        </Box>
    );
};